import React, { useState } from 'react';
import { Building2, Coins } from 'lucide-react';
import { GameProvider } from '../../../types';
import GameProviderList from './GameProviderList';
import AgentBalancesTab from './AgentBalancesTab';
import CreateGameProviderModal from './CreateGameProviderModal';
import UpdateGameProviderModal from './UpdateGameProviderModal';
import DeleteGameProviderModal from './DeleteGameProviderModal';
import { usePermission } from '../../../hooks/useAuth';
import { PageHeader } from '@kioskgaming/ui';
import AdminTabs, { type AdminTabItem } from '../../ui/AdminTabs';
import AccessDeniedPanel from '../../ui/AccessDeniedPanel';

type TabId = 'providers' | 'agent-balances';

const GameProvidersManagement: React.FC = () => {
  const canManageProviders = usePermission('gameProviderManagement');
  const [activeTab, setActiveTab] = useState<TabId>('providers');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<GameProvider | null>(null);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  if (!canManageProviders) {
    return (
      <AccessDeniedPanel message="You don&apos;t have permission to manage game providers." />
    );
  }

  const handleCreate = () => {
    setIsCreateOpen(true);
  };

  const handleEdit = (provider: GameProvider) => {
    setSelectedProvider(provider);
    setIsUpdateOpen(true);
  };

  const handleDelete = (provider: GameProvider) => {
    setSelectedProvider(provider);
    setIsDeleteOpen(true);
  };

  const handleSuccess = () => {
    setSelectedProvider(null);
  };

  const tabs: AdminTabItem<TabId>[] = [
    { id: 'providers', label: 'Providers', icon: Building2 },
    { id: 'agent-balances', label: 'Agent Balances', icon: Coins },
  ];

  return (
    <div className="space-y-8">
      <PageHeader
        title="Game Provider Management"
        description="Configure kiosk ordering and badges, then review how agent balances map to each game."
      />

      <AdminTabs<TabId>
        variant="segmented"
        tabs={tabs}
        value={activeTab}
        onChange={setActiveTab}
      />

      {activeTab === 'providers' && (
        <>
          <GameProviderList
            onCreate={handleCreate}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
          <CreateGameProviderModal
            isOpen={isCreateOpen}
            onClose={() => setIsCreateOpen(false)}
            onSuccess={handleSuccess}
          />
          <UpdateGameProviderModal
            provider={selectedProvider}
            isOpen={isUpdateOpen}
            onClose={() => {
              setIsUpdateOpen(false);
              setSelectedProvider(null);
            }}
            onSuccess={handleSuccess}
          />
          <DeleteGameProviderModal
            provider={selectedProvider}
            isOpen={isDeleteOpen}
            onClose={() => {
              setIsDeleteOpen(false);
              setSelectedProvider(null);
            }}
            onSuccess={handleSuccess}
          />
        </>
      )}

      {activeTab === 'agent-balances' && <AgentBalancesTab />}
    </div>
  );
};

export default GameProvidersManagement;

